import { getJsHeapMb } from '../../monitoring/renderer-vitals';
import { pageLoadTimestamp } from '../../monitoring/renderer-diagnostic-context';
import { getGpuIdentity, getWebGLContextStats } from '../../monitoring/webgl-context-registry';
import type { GpuIdentity, WebGLContextStats } from '../../monitoring/webgl-context-registry';
import { getMapLifecycleStats } from '../../monitoring/map-lifecycle-tracker';
import type { MapLifecycleStats } from '../../monitoring/map-lifecycle-tracker';
import { getMapErrorCounts } from './map-diagnostic-context';
import type { MapErrorCounts } from './map-diagnostic-context';
import { getMapInstanceCounts } from './map-instance-registry';
import type { MapInstanceCounts } from './map-instance-registry';

/**
 * A black map that fires within this long of the MapView being created is treated
 * as a setup/initialisation failure rather than a resource-accretion failure —
 * these look like a different failure mode and we want to be able to query them
 * apart.
 */
export const IMMEDIATE_FAILURE_MS = 10_000;
/** Upper bound for "happened early in this map's life, but not at setup". */
export const EARLY_FAILURE_MS = 60_000;

export type FailureTiming = 'immediate' | 'early' | 'aged' | 'unknown';

export function classifyFailureTiming(mapAgeMs: number | null): FailureTiming {
    if (mapAgeMs === null) {
        return 'unknown';
    }
    if (mapAgeMs < IMMEDIATE_FAILURE_MS) {
        return 'immediate';
    }
    if (mapAgeMs < EARLY_FAILURE_MS) {
        return 'early';
    }
    return 'aged';
}

export type EpisodeDiagnostics = MapErrorCounts &
    WebGLContextStats &
    GpuIdentity &
    MapLifecycleStats &
    MapInstanceCounts & {
        /** Age of THIS episode's MapView at failure time. Null when its creation time is
         * unknown. (This used to be the age of whichever map — of any surface — was
         * created most recently, so every new preview map reset it.) */
        mapAgeMs: number | null;
        /** Bucketed {@link mapAgeMs} so dashboards can group without range queries. */
        failureTiming: FailureTiming;
        /** Age of the page/session, to distinguish a fresh map in an old session from a fresh session. */
        sessionAgeMs: number;
        jsHeapUsedMb?: number;
        jsHeapTotalMb?: number;
        jsHeapLimitMb?: number;
        /** Rendered size of the map container at failure time.
         *
         * THE ZERO-AREA TEST. With `firstResponderMapContainerFixedPositionEnabled`
         * off — which is most users — the hidden First Responder map is collapsed to
         * `visibility:hidden; width:0; height:0`, and Esri responds by suspending the
         * LayerView and snapshotting its WebGL render buffers. If failures with zero
         * live contexts land overwhelmingly on `0`, that path is the cause and the
         * remedy is a container change rather than anything to do with context
         * lifetime. Undefined when no container was supplied. */
        containerWidth?: number;
        containerHeight?: number;
        /** `document.visibilityState`. Deliberately separate from
         * `wasMapHiddenAtFailure`, which is *our* app-level map visibility: this is the
         * whole renderer being backgrounded, where Chromium may reclaim GPU resources
         * on its own. The two are indistinguishable without both fields. */
        documentVisibilityState?: string;
    };

type EpisodeDiagnosticsInput = {
    /** The map's container element, read for its rendered size. Optional because not
     * every caller has one to hand. */
    container?: HTMLElement | null;
    /** When the episode's own MapView was created. */
    mapCreatedAtMs?: number;
};

/** Rendered size of the container, or nothing if it cannot be read. */
function getContainerSize(
    container: HTMLElement | null | undefined
): { containerWidth: number; containerHeight: number } | undefined {
    if (!container) {
        return undefined;
    }
    try {
        // `clientWidth`/`clientHeight` can force a layout. Acceptable here: this runs
        // only on black-map episode events, which are rare and already degraded.
        return { containerWidth: container.clientWidth, containerHeight: container.clientHeight };
    } catch {
        return undefined;
    }
}

function getDocumentVisibilityState(): string | undefined {
    try {
        return typeof document === 'undefined' ? undefined : document.visibilityState;
    } catch {
        return undefined;
    }
}

/**
 * Snapshots the cheap, renderer-only diagnostics worth having on every black-map
 * episode event.
 *
 * Deliberately NOT gated behind the `diagnosticContextEnabled` feature flag: every
 * value here is a plain in-process read (counters, `performance.memory`, the context
 * registry) with no Electron IPC, so it works in a web tab and for departments that
 * cannot enable diagnostic-context collection. The flag exists to gate the
 * expensive main-process IPC round-trip, which this does not do.
 *
 * Nothing here may call `getContext`: on a canvas that has no context yet, that
 * creates one — during the exact context crisis these events describe. Live context
 * counts come from the registry, which observes contexts without touching canvases.
 *
 * All fields are counts, durations and sizes — no PII/CJI.
 */
export function getEpisodeDiagnostics({
    container,
    mapCreatedAtMs,
}: EpisodeDiagnosticsInput = {}): EpisodeDiagnostics {
    const mapAgeMs = mapCreatedAtMs === undefined ? null : Date.now() - mapCreatedAtMs;
    const heap = getJsHeapMb();

    return {
        ...getMapErrorCounts(),
        // Instrumented WebGL context census — the authoritative read on whether
        // contexts are being leaked (canvas counts cannot see an orphaned context).
        ...getWebGLContextStats(),
        // Cached from before the failure: a lost context can no longer be asked.
        ...getGpuIdentity(),
        // How the user has been using the map (navigation churn, hidden vs visible).
        ...getMapLifecycleStats(),
        // How many MapViews we believe are alive, split by surface.
        ...getMapInstanceCounts(),
        mapAgeMs,
        failureTiming: classifyFailureTiming(mapAgeMs),
        sessionAgeMs: Date.now() - pageLoadTimestamp,
        ...(heap ?? {}),
        ...(getContainerSize(container) ?? {}),
        documentVisibilityState: getDocumentVisibilityState(),
    };
}
